import React from 'react';
import { useDispatch } from 'react-redux';

import {
  addDiscountAction,
  addManualItem,
  changeExtraQuantity,
  changeOrderPPAction,
  changeProductQuantity,
  changeSalePrice,
  removeDiscountAction,
  removeExtraAction,
  removeProductAction,
} from 'actions/carts';
import { openModalAction } from 'actions/ui';
import { currencyFormatter } from 'helpers/formatters';
import isEmpty from 'lodash/isEmpty';
import { reduceCartOrders } from 'utils';
import { Trans, t } from '@lingui/macro';

import Hyperlink from 'components/Hyperlink';

import { discountLabels } from 'constants/discount';

import CartItem from './CartItem';
import './CartList.styl';
import DiscountForm from './DiscountForm';
import ManualItemForm from './ManualItemForm';
import { TYPE_ID_MOBILE_RECYCLE } from 'constants/discount';
import { getCDNBaseURL } from 'envUtils';

const CartList = ({
  language,
  currency,
  cart,
  cartUUID,
  totalItems,
  availableDiscounts,
  actionable,
  useQuantities,
  canChangeSalePrice,
  selectedPaymentPlan,
  canQuickQuote,
  displayMargin,
}) => {
  const dispatch = useDispatch();
  const openDiscount = (ev) => {
    ev.preventDefault();
    dispatch(openModalAction('discountForm'));
  };

  const openManualItem = (ev) => {
    ev.preventDefault();
    dispatch(openModalAction('manualItemForm'));
  };
  const orders = cart.orders;
  const {
    products,
    extras,
    discounts,
    totalPrincipal,
    totalMonthlyPayment,
    openingFeeAmount,
    totalDiscounts,
    totalMonthlyDiscounts,
  } = reduceCartOrders(orders);

  const orderUUID = cart.uuid;
  const pricingIncludesTax = cart.pricingIncludesTax;
  const orderWithUpfront = orders.find(
    (order) => order.financing?.openingFeePercentage > 0,
  );
  const openingFeePercentage =
    orderWithUpfront?.financing?.openingFeePercentage;
  const paymentPlanName = selectedPaymentPlan?.name;
  const colspanCount = () => {
    let max = 6;
    if (!displayMargin) {
      max -= 3;
    }
    if (!useQuantities) {
      max -= 1;
    }
    return max;
  };

  // Function to render the "Excl. VAT" row based on displayMargin and useQuantities
  const renderExclVATRow = (displayMargin, useQuantities) => {
    // Define column spans based on whether displayMargin is true or false
    const columns = displayMargin
      ? [
          { colSpan: 2 }, // First two columns
          { colSpan: 3 }, // Middle columns
          { colSpan: useQuantities ? 2 : 1 }, // Adjusted for quantity usage
          { colSpan: 1 }, // Last column
        ]
      : [
          { colSpan: 2 }, // First two columns
          { colSpan: useQuantities ? 2 : 1 }, // Adjusted for quantity usage
          { colSpan: 1 }, // Last column
        ];

    return (
      <tr className='priceExclVATRow'>
        {/* Render table cells dynamically based on columns array */}
        {columns.map((tdProps, index) => (
          <td key={index} {...tdProps}>
            {/* Translatable "Excl. VAT" text */}
            <Trans id='sale.summary.excl.vat'>Excl. VAT</Trans>
          </td>
        ))}
      </tr>
    );
  };

  return (
    <>
      <table className='cart-details-table'>
        <thead>
          <tr>
            <th>
              <h3>
                {cartUUID} / {paymentPlanName}
              </h3>
            </th>
            {displayMargin && (
              <>
                <th>
                  <Trans id='sale.summary.cost'>Unit Cost Price</Trans>
                </th>
                <th>
                  <Trans id='sale.summary.margin'>Margin Amount</Trans>
                </th>
                <th>
                  <Trans id='sale.summary.margin.percentage'>Margin %</Trans>
                </th>
              </>
            )}
            <th>
              <Trans id='sale.summary.unit'>Unit Sale Price</Trans>
            </th>
            {useQuantities && (
              <th>
                <Trans id='sale.summary.quantity'>Quantity</Trans>
              </th>
            )}
            <th>
              <Trans id='sale.summary.total.sale.price'>Total Sale Price</Trans>
            </th>
            <th>
              <Trans id='sale.summary.per.month'>Per month</Trans>
            </th>
          </tr>
        </thead>
        {!pricingIncludesTax && renderExclVATRow(displayMargin, useQuantities)}
        <tbody>
          {/*product mapping*/}
          {products?.map((product) => (
            <CartItem
              cartUUID={cartUUID}
              UUID={product.uuid}
              key={product.uuid}
              monthlyPrice={product.monthly_price}
              price={product.cash_price}
              totalPrice={product.totalCashPrice}
              imgURL={product.img_url}
              name={product.name}
              actionable={actionable}
              // TODO make this component reliant only on class functions
              // in order to remove arrow functions and enforce this rule
              // eslint-disable-next-line react/jsx-no-bind
              actionRemove={() =>
                dispatch(removeProductAction(cartUUID, product.uuid, true))
              }
              quantity={product.quantity}
              currency={currency}
              language={language}
              changeQuantity={(cartUUID, UUID, quantity) => {
                useQuantities &&
                  dispatch(changeProductQuantity(cartUUID, UUID, quantity));
              }}
              useQuantities={useQuantities}
              canChangeSalePrice={canChangeSalePrice}
              changeSalePrice={(cartUUID, UUID, salePrice) => {
                canChangeSalePrice &&
                  dispatch(changeSalePrice(cartUUID, UUID, salePrice));
              }}
              marginAmount={product.retailerMarkup}
              costPrice={product.costPriceExclVAT}
              displayMargin={displayMargin}
              marginPercent={product.marginPercent}
            />
          ))}
          {/*extras mapping*/}
          {extras?.map((extra) => (
            <CartItem
              cartUUID={cartUUID}
              UUID={extra.uuid}
              key={extra.uuid}
              monthlyPrice={extra.monthly_price}
              price={extra.value}
              totalPrice={extra.totalCashPrice}
              imgURL={extra.img_url}
              name={extra.description}
              isCTO={extra.isCTO}
              actionable={actionable}
              // eslint-disable-next-line react/jsx-no-bind
              actionRemove={() =>
                dispatch(
                  removeExtraAction(cartUUID, extra.uuid, () =>
                    dispatch(changeOrderPPAction(cartUUID)),
                  ),
                )
              }
              quantity={extra.quantity}
              currency={currency}
              language={language}
              changeQuantity={(cartUUID, UUID, quantity) => {
                useQuantities &&
                  dispatch(changeExtraQuantity(cartUUID, UUID, quantity));
              }}
              useQuantities={useQuantities}
              canChangeSalePrice={canChangeSalePrice}
              changeSalePrice={(cartUUID, UUID, salePrice) => {
                canChangeSalePrice &&
                  dispatch(changeSalePrice(cartUUID, UUID, salePrice));
              }}
              marginAmount={extra.retailerMarkup}
              costPrice={extra.costPriceExclVAT}
              displayMargin={displayMargin}
              marginPercent={extra.marginPercent}
            />
          ))}
          {/*discounts mapping*/}
          {discounts?.map((discount) => (
            <CartItem
              imgURL={
                discount.type_id === +TYPE_ID_MOBILE_RECYCLE
                  ? `${getCDNBaseURL()}svg/icon-tradein-device.svg`
                  : `${getCDNBaseURL()}svg/Discount.svg`
              }
              cartUUID={cartUUID}
              UUID={discount.uuid}
              key={discount.uuid}
              monthlyPrice={discount.monthly_value}
              price={discount.value}
              name={
                <Trans id='sale.summary.discount'>{`${
                  discountLabels()[discount.type_id]
                } discount`}</Trans>
              }
              actionable={actionable && !discount.locked}
              // eslint-disable-next-line react/jsx-no-bind
              actionRemove={() =>
                dispatch(
                  removeDiscountAction(orderUUID, discount.uuid, () =>
                    dispatch(changeOrderPPAction(cartUUID)),
                  ),
                )
              }
              useQuantities={useQuantities}
              currency={currency}
              language={language}
              displayMargin={displayMargin}
            />
          ))}
          {/*action btns*/}
          <tr>
            <div className='cart-list-action-btns'>
              {actionable && canQuickQuote && (
                <Hyperlink
                  variant='plus'
                  classes='cart-list-add-discount'
                  content={'Add item'}
                  onClick={openManualItem}
                />
              )}

              {actionable &&
                !isEmpty(availableDiscounts) &&
                totalItems !== 0 && (
                  <Hyperlink
                    variant='plus'
                    classes='cart-list-add-discount'
                    content={t({
                      id: 'sale.summary.add.discount',
                      message: 'Add discount',
                    })}
                    onClick={openDiscount}
                  />
                )}
            </div>
          </tr>
        </tbody>
        {/*sales summary*/}
        <tfoot>
          {/*discounts summary*/}
          <tr>
            <th colSpan={colspanCount()} scope='row'>
              {t({
                id: 'sale.summary.discounts.total',
                message: 'Discounts total',
              })}
            </th>
            <td>{currencyFormatter(totalDiscounts, currency, language)}</td>
            <td>
              {currencyFormatter(totalMonthlyDiscounts, currency, language)}
            </td>
          </tr>
          {/*fees summary*/}
          {openingFeeAmount > 0 && openingFeePercentage > 0 && (
            <tr>
              <th colSpan={colspanCount()} scope='row'>
                <Trans id='product.upfront'>{`${openingFeePercentage}% Upfront payment`}</Trans>
              </th>

              <td>{currencyFormatter(openingFeeAmount, currency, language)}</td>
            </tr>
          )}
          {/*financing summary*/}
          <tr>
            <th colSpan={colspanCount()} scope='row'>
              {t({
                id: 'sale.summary.total',
                message: 'Total',
              })}
            </th>
            <td>{currencyFormatter(totalPrincipal, currency, language)}</td>
            <td>
              {currencyFormatter(totalMonthlyPayment, currency, language)}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      {actionable && !isEmpty(availableDiscounts) && (
        <DiscountForm
          availableDiscounts={availableDiscounts}
          addDiscount={(uuid, discount) =>
            dispatch(addDiscountAction(uuid, discount))
          }
          order={orders[0]}
          currency={currency}
          language={language}
          removeDiscount={(uuid, discount) =>
            dispatch(removeDiscountAction(uuid, discount))
          }
          // eslint-disable-next-line react/jsx-no-bind
          onCloseDiscount={() => dispatch(changeOrderPPAction(cartUUID))}
        />
      )}

      {actionable && canQuickQuote && (
        <ManualItemForm
          cartUUID={cartUUID}
          addItem={(cartUUID, item) => dispatch(addManualItem(cartUUID, item))}
          onClose={() => dispatch(changeOrderPPAction(cartUUID))}
        />
      )}
    </>
  );
};

export default CartList;
